import { config } from "@/config";
import { Environment } from "@/types/runtime-config";

type LogLevel = "log" | "error" | "warn" | "info";

const redact = (key: string): [string, boolean] => {
  if (
    key.toLowerCase().includes("password") ||
    key.toLowerCase().includes("captcha") ||
    key.toLowerCase().includes("token") ||
    key.toLowerCase().includes("secret") ||
    key.toLowerCase().includes("key")
  ) {
    return ["[REDACTED]", true];
  }

  return [key, false];
};

const sanitize = (data: unknown): unknown => {
  if (typeof data === "string") {
    return redact(data);
  }

  if (Array.isArray(data)) {
    return data.map(sanitize);
  }

  if (data && typeof data === "object") {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      // Hide sensitive fields
      let redactedValue,
        isRedacted = redact(key);

      if (isRedacted) {
        sanitized[key] = redactedValue;
      } else {
        sanitized[key] = sanitize(value);
      }
    }

    return sanitized;
  }

  return data;
};

const log = (level: LogLevel, message: string, ...args: unknown[]): void => {
  if (config.environment === Environment.Prod) {
    return;
  }

  const sanitizedArgs = args.map((arg) => {
    if (typeof arg === "object" && arg !== null) {
      return sanitize(arg);
    }
    return arg;
  });

  console[level](message, ...sanitizedArgs);
};

export const logger = {
  log: (message: string, ...args: unknown[]) => log("log", message, ...args),
  error: (message: string, ...args: unknown[]) => log("error", message, ...args),
  warn: (message: string, ...args: unknown[]) => log("warn", message, ...args),
  info: (message: string, ...args: unknown[]) => log("info", message, ...args),
};
